import { useEffect, useMemo } from 'react';
import { UIMatch, useLocation, useMatches } from 'react-router-dom';

import { logError } from 'services/logging/logging';
import {
  clearBreadcrumbs,
  getPreviousMatches,
  setBreadcrumb,
  setPreviousMatches,
} from 'stores/breadcrumbs/breadcrumbs';
import { BreadcrumbConfig, MatchWithBreadcrumb } from 'support/types/breadcrumbs';

export const processMatchWithData = async (
  match: MatchWithBreadcrumb<unknown>,
  index: number,
): Promise<{ index: number; breadcrumb: BreadcrumbConfig } | null> => {
  try {
    if (!hasData(match)) {
      return null;
    }

    const resolvedData = await match.data.data;
    const config = match.handle.getBreadcrumbConfig(match.params, resolvedData);
    return {
      index,
      breadcrumb: { label: config.label, labelAdornment: config.labelAdornment, to: config.to, key: match.id },
    };
  } catch (error) {
    logError(error);
    const config = match.handle.getBreadcrumbConfig(match.params, undefined);
    return {
      index,
      breadcrumb: { label: config.label, labelAdornment: config.labelAdornment, to: config.to, key: match.id },
    };
  }
};

/**
 * Type guard to check if a match has valid breadcrumb configuration
 */
export const hasValidBreadcrumbHandle = (match: UIMatch): match is MatchWithBreadcrumb => {
  return !!(
    match.handle &&
    typeof match.handle === 'object' &&
    'getBreadcrumbConfig' in match.handle &&
    typeof match.handle.getBreadcrumbConfig === 'function'
  );
};

/**
 * Type guard to check if data is available and is an AsyncDataContainer
 */
export const hasData = (match: UIMatch): match is MatchWithBreadcrumb<unknown> => {
  const data = match.data;
  return typeof data === 'object' && data !== null && 'data' in data && data.data instanceof Promise;
};

/**
 * Merges previous matches with current matches, avoiding duplicates
 * This is important because we have user workflows where the route might not match the url
 * Example:
 *  - User navigates to a document from the "Blocked transactions" page
 *  - The route is /transactions/messages/:documentId but the user is coming from /transactions/blocked
 */
export const mergeMatches = (previousMatches: Array<UIMatch>, currentMatches: Array<UIMatch>): Array<UIMatch> => {
  const slicedCurrentMatches = currentMatches.slice(-2);

  // Check for backward navigation by comparing pathnames
  // If current's deepest pathname is a prefix of previous's deepest pathname, we're navigating backward
  const currentDeepestPathname = currentMatches[currentMatches.length - 1]?.pathname ?? '';
  const previousDeepestPathname = previousMatches[previousMatches.length - 1]?.pathname ?? '';

  if (
    currentDeepestPathname &&
    previousDeepestPathname &&
    previousDeepestPathname.startsWith(currentDeepestPathname) &&
    previousDeepestPathname !== currentDeepestPathname
  ) {
    // Navigating backward - just use current matches
    return currentMatches;
  }

  // Find the index of the last current match in previous matches
  const lastCurrentMatchIndex = previousMatches.findIndex(
    (prevMatch) => prevMatch.id === slicedCurrentMatches[slicedCurrentMatches.length - 1]?.id,
  );
  // If found, slice previous matches up to and including that index
  const filteredPreviousMatches =
    lastCurrentMatchIndex >= 0 ? previousMatches.slice(0, lastCurrentMatchIndex + 1) : previousMatches;

  // Start with filtered previous matches
  const merged = [...filteredPreviousMatches];

  slicedCurrentMatches.forEach((currentMatch) => {
    const duplicateIndex = merged.findIndex((prevMatch) => prevMatch.id === currentMatch.id);
    if (duplicateIndex >= 0) {
      merged[duplicateIndex] = currentMatch;
    } else {
      merged.push(currentMatch);
    }
  });

  return merged;
};

/**
 * Determines which matches to use based on history state and breadcrumb settings
 */
export const useProcessedMatches = () => {
  const currentMatches = useMatches();
  const location = useLocation();

  return useMemo(() => {
    const previousMatches = getPreviousMatches();
    // If no previous matches or not keeping breadcrumbs, use current matches
    if (!previousMatches || previousMatches.length === 0) {
      return currentMatches;
    }

    if (!location.state?.keepBreadcrumbs) {
      return currentMatches;
    }

    // Merge previous and current matches
    return mergeMatches(previousMatches, currentMatches);
  }, [currentMatches, location]);
};

/**
 * Custom hook that generates breadcrumbs from matches
 */
export const useGenerateBreadcrumbs = (matches: Array<UIMatch>) => {
  useEffect(() => {
    let cancelled = false;
    // Reset crumbs when matches change
    clearBreadcrumbs();

    const validMatches = matches.filter(hasValidBreadcrumbHandle);
    validMatches.forEach((match, index) => {
      if (hasData(match)) {
        processMatchWithData(match, index).then((result) => {
          if (result && !cancelled) {
            setBreadcrumb(result.breadcrumb, result.index);
          }
        });
      } else {
        const config = match.handle.getBreadcrumbConfig(match.params);
        setBreadcrumb(config, index);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [matches]);
};

export const useSavePreviousMatches = (matches: Array<UIMatch>) => {
  const { pathname } = useLocation();
  useEffect(() => {
    setPreviousMatches(matches);
  }, [pathname, matches]);
};
